/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   useMaskedInput
 */

import { useCallback, useEffect, useState } from 'react'

import { type FormatChars, selectMaskedValue } from './selectMaskedValue'

interface UseMaskedInputProps {
  formatChars?: FormatChars
  mask: string
  onChange: (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ClipboardEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => void
  value: string | undefined
}

export const useMaskedInput = ({
  formatChars,
  mask,
  onChange: onChangeProp,
  value,
}: UseMaskedInputProps) => {
  const [maskedValue, setMaskedValue] = useState(selectMaskedValue(value, mask, formatChars))
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | React.ClipboardEvent<HTMLInputElement>) => {
      const value = (e.currentTarget ?? e.target).value.replaceAll('-', '')
      const newValue = selectMaskedValue(value, mask, formatChars)
      setMaskedValue(newValue)
      if (onChangeProp) {
        ;(e.currentTarget ?? e.target).value = newValue
        onChangeProp(e)
      }
    },
    [formatChars, mask, onChangeProp]
  )
  useEffect(() => {
    const newValue = selectMaskedValue(value, mask, formatChars)
    setMaskedValue(newValue)
  }, [formatChars, mask, value])
  return { maskedValue, onChange }
}
